import { useState } from 'react';
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder } from '@fortawesome/free-solid-svg-icons';
import { DropDownBox } from '@apply/components';
import { FileSelectDialog } from '../../../common/SharedComponents';
import { RequiredRule } from 'devextreme-react/validator';

export interface TaxYearReview {
    year: number;
    returnFile: any;
    transcriptFile: any;
}

interface TranscriptReviewInputFormParameters {
    onSubmit: (taxDocuments: TaxYearReview[]) => void;
}

export const TranscriptReviewInputForm: React.FC<TranscriptReviewInputFormParameters> = ({ onSubmit }) => {
    const returnFileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        fileTypeName: 'Return File',
    };
    const transcriptFileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        fileTypeName: 'Transcript File',
    };

    const [usedTaxYears, set_usedTaxYears] = useState([] as number[]);
    const [returnFileSelectOptions, set_returnFileSelectOptions] = useState(returnFileUploadOptions);
    const [returnFileCell, set_returnFileCell] = useState();
    const [transcriptFileSelectOptions, set_transcriptFileSelectOptions] = useState(transcriptFileUploadOptions);
    const [transcriptFileCell, set_transcriptFileCell] = useState();
    const [taxYearDocs, set_taxYearDocs] = useState([] as TaxYearReview[]);
    const [selectedTaxYear, set_selectedTaxYear] = useState<number | null>(null);

    const yearsAgo2021 = new Date().getFullYear() - 2021;
    const availableTaxYears = Array.from({ length: yearsAgo2021 + 1 }, (_, i) => new Date().getFullYear() - i).filter(
        (x) => !usedTaxYears.includes(x)
    );

    const styles = {
        form: { paddingTop: '10px' },
        formRow: { marginBottom: '0' },
    };

    const onReturnFileChange = (id, files) => {
        returnFileCell?.setValue(files[0]);
        set_returnFileSelectOptions({ ...returnFileSelectOptions, isOpen: false });
    };

    const onReturnFileSelectCancel = () => {
        set_returnFileSelectOptions({ ...returnFileSelectOptions, isOpen: false });
    };

    const onTranscriptFileChange = (id, files) => {
        transcriptFileCell?.setValue(files[0]);
        set_transcriptFileSelectOptions({ ...transcriptFileSelectOptions, isOpen: false });
    };

    const onTranscriptFileSelectCancel = () => {
        set_transcriptFileSelectOptions({ ...transcriptFileSelectOptions, isOpen: false });
    };

    const editTaxYearRender = (cell: any) => {
        return (
            <DropDownBox
                id='year'
                value={selectedTaxYear}
                onChange={(e) => {
                    cell.setValue(Number(e));
                    set_selectedTaxYear(Number(e));
                }}
                dataSource={availableTaxYears}
            />
        );
    };

    const editReturnFileRender = (cell: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <button
                    type='button'
                    title='Upload Tax Return File'
                    onClick={() => {
                        set_returnFileCell(cell);
                        set_returnFileSelectOptions({ ...returnFileSelectOptions, isOpen: true });
                    }}
                >
                    Select Tax Return &nbsp;
                    <FontAwesomeIcon
                        icon={faFolder}
                        className='admin-icon-button'
                    />
                </button>
                <br />
                <strong>{cell.value?.name}</strong>
            </div>
        );
    };

    const editTranscriptFileRender = (cell: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <button
                    type='button'
                    title='Upload Tax Transcript File'
                    onClick={() => {
                        set_transcriptFileCell(cell);
                        set_transcriptFileSelectOptions({ ...transcriptFileSelectOptions, isOpen: true });
                    }}
                >
                    Select Tax Transcript &nbsp;
                    <FontAwesomeIcon
                        icon={faFolder}
                        className='admin-icon-button'
                    />
                </button>
                <br />
                <strong>{cell.value?.name}</strong>
            </div>
        );
    };

    return (
        <>
            <h2>Input Tax Documents</h2>
            <div
                style={styles.form}
                className={'standard-form'}
            >
                <DataGrid
                    id={'taxYearsGrid'}
                    keyExpr='year'
                    dataSource={taxYearDocs}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    remoteOperations={false}
                    noDataText='No tax documents have been added'
                    onRowRemoved={(e) => {
                        set_usedTaxYears((prev) => prev.filter((p) => p !== e.data.year));
                    }}
                    onRowInserted={(e) => {
                        set_usedTaxYears((prev) => [...prev, e.data.year]);
                        set_selectedTaxYear(null);
                    }}
                >
                    <Editing
                        mode='row'
                        allowAdding={true}
                        allowDeleting={true}
                        allowUpdating={false}
                        confirmDelete={false}
                        useIcons={true}
                        newRowPosition={'first'}
                    />
                    <Column
                        dataField='year'
                        caption='Tax Year'
                        cellRender={(cell) => <span>{cell.value}</span>}
                        editCellRender={editTaxYearRender}
                        alignment='left'
                    >
                        <RequiredRule message='Tax Year is required' />
                    </Column>
                    <Column
                        dataField='returnFile'
                        caption='Return File'
                        cellRender={(cell) => <span>{cell.value?.name}</span>}
                        editCellRender={editReturnFileRender}
                    >
                        <RequiredRule message='Tax Return File is required' />
                    </Column>
                    <Column
                        dataField='transcriptFile'
                        caption='Transcript File'
                        cellRender={(cell) => <span>{cell.value?.name}</span>}
                        editCellRender={editTranscriptFileRender}
                    >
                        <RequiredRule message='Tax Transcript File is required' />
                    </Column>
                </DataGrid>
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div
                        className='columns small-12 large-12'
                        style={{
                            paddingTop: '15px',
                            paddingBottom: '0px',
                            textAlign: 'center',
                        }}
                    >
                        <button
                            type='button'
                            className='button hollow'
                            title='Review Documents'
                            onClick={() => taxYearDocs.length && onSubmit(taxYearDocs)}
                            style={{ width: '100%' }}
                        >
                            Review Documents
                        </button>
                    </div>
                </div>
            </div>

            <FileSelectDialog
                options={returnFileSelectOptions}
                onSubmit={onReturnFileChange}
                onCancel={onReturnFileSelectCancel}
            />
            <FileSelectDialog
                options={transcriptFileSelectOptions}
                onSubmit={onTranscriptFileChange}
                onCancel={onTranscriptFileSelectCancel}
            />
        </>
    );
};

export default TranscriptReviewInputForm;
